import React, { useDeferredValue, useEffect, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Download,
  Euro,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import {
  exportAdminBillingCsv,
  fetchAdminBilling,
  type AdminBillingPartnerState,
  type AdminBillingPeriod,
} from "@/api/adminBilling";
import { AdminEmptyState, AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/StatCard";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { BillingWorkspace } from "@/components/admin/BillingWorkspace";

const formatNumber = new Intl.NumberFormat("el-GR").format;
const formatCurrency = new Intl.NumberFormat("el-GR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2,
}).format;

const PERIODS: Array<{ value: AdminBillingPeriod; label: string }> = [
  { value: "current_week", label: "Τρέχουσα εβδομάδα" },
  { value: "current_month", label: "Τρέχων μήνας" },
  { value: "previous_month", label: "Προηγούμενος μήνας" },
  { value: "current_quarter", label: "Τρέχον τρίμηνο" },
  { value: "current_year", label: "Τρέχον έτος" },
  { value: "all", label: "Όλο το ιστορικό" },
  { value: "custom", label: "Προσαρμοσμένο" },
];

const PARTNER_STATES: Array<{ value: AdminBillingPartnerState; label: string }> = [
  { value: "with_leads", label: "Μόνο με χρέωση" },
  { value: "all", label: "Όλοι οι συνεργάτες" },
  { value: "active", label: "Μόνο ενεργοί" },
  { value: "inactive", label: "Μόνο ανενεργοί" },
];

const inputDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const displayDate = (value: string | null): string => {
  if (!value) return "—";
  const date = value.slice(0, 10).split("-");
  return date.length === 3 ? `${date[2]}/${date[1]}/${date[0]}` : value;
};

const BillingPage: React.FC = () => {
  const [view, setView] = useState<"invoices" | "overview">("invoices");
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<AdminBillingPeriod>("current_month");
  const [partnerState, setPartnerState] = useState<AdminBillingPartnerState>("with_leads");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim());
  const [page, setPage] = useState(1);
  const [customFrom, setCustomFrom] = useState(() => {
    const date = new Date();
    return inputDate(new Date(date.getFullYear(), date.getMonth(), 1));
  });
  const [customTo, setCustomTo] = useState(() => inputDate(new Date()));
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const queryInput = {
    period,
    state: partnerState,
    search: deferredSearch,
    page,
    from: period === "custom" ? customFrom : undefined,
    to: period === "custom" ? customTo : undefined,
  };

  const billingQuery = useQuery({
    queryKey: ["admin-billing", queryInput],
    queryFn: () => fetchAdminBilling(queryInput),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    enabled: period !== "custom" || Boolean(customFrom && customTo && customFrom <= customTo),
  });

  useEffect(() => setPage(1), [period, partnerState, deferredSearch, customFrom, customTo]);
  useEffect(() => {
    const pages = billingQuery.data?.pagination.pages ?? 1;
    if (page > pages) setPage(pages);
  }, [billingQuery.data?.pagination.pages, page]);

  const data = billingQuery.data;
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportAdminBillingCsv(queryInput);
      toast({ title: "Το CSV δημιουργήθηκε", description: `Πλήρη στοιχεία για ${data?.range.label || "την επιλεγμένη περίοδο"}.` });
    } catch (error) {
      toast({
        title: "Η εξαγωγή δεν ολοκληρώθηκε",
        description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        eyebrow="Οικονομική εικόνα"
        title="Χρεώσεις συνεργατών"
        description="Πλήρης server-side υπολογισμός των ανατεθειμένων leads. Τα leads χωρίς συνεργάτη εμφανίζονται ξεχωριστά και δεν χρεώνονται."
        icon={<Euro />}
        iconGradient="emerald"
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button type="button" variant="outline" className="h-11 w-full gap-2 sm:w-auto" onClick={() => { void billingQuery.refetch(); void queryClient.invalidateQueries({ queryKey: ["billing-workspace"] }); }} disabled={billingQuery.isFetching}>
              <RefreshCw className={cn("h-4 w-4", billingQuery.isFetching && "animate-spin")} /> Ανανέωση
            </Button>
            <Button type="button" className="h-11 w-full gap-2 bg-emerald-600 hover:bg-emerald-700 sm:w-auto" onClick={() => void handleExport()} disabled={!data || isExporting}>
              <Download className={cn("h-4 w-4", isExporting && "animate-pulse")} /> Πλήρες CSV
            </Button>
          </div>
        }
      />

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="grid gap-3 lg:grid-cols-[220px_190px_minmax(220px,1fr)]">
            <Select value={period} onValueChange={(value) => setPeriod(value as AdminBillingPeriod)}>
              <SelectTrigger className="h-11" aria-label="Περίοδος χρέωσης"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={partnerState} onValueChange={(value) => setPartnerState(value as AdminBillingPartnerState)}>
              <SelectTrigger className="h-11" aria-label="Κατάσταση συνεργάτη"><SelectValue /></SelectTrigger>
              <SelectContent>{PARTNER_STATES.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Αναζήτηση συνεργάτη ή περιοχής" className="h-11 pl-9" aria-label="Αναζήτηση συνεργάτη" />
            </div>
          </div>

          {period === "custom" && (
            <div className="mt-3 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center">
              <CalendarRange className="hidden h-4 w-4 text-slate-500 sm:block" />
              <label className="flex flex-1 items-center gap-2 text-sm text-slate-600">
                <span className="w-10">Από</span>
                <input type="date" value={customFrom} max={customTo} onChange={(event) => setCustomFrom(event.target.value)} className="h-11 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-slate-900" />
              </label>
              <label className="flex flex-1 items-center gap-2 text-sm text-slate-600">
                <span className="w-10">Έως</span>
                <input type="date" value={customTo} min={customFrom} onChange={(event) => setCustomTo(event.target.value)} className="h-11 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-slate-900" />
              </label>
            </div>
          )}

          <div className="mt-3 flex flex-col gap-1 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-950 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-semibold">{data?.range.label || "Η επιλεγμένη περίοδος"}</span>
            <span className="text-xs text-emerald-800">Η περίοδος αφορά την ημερομηνία δημιουργίας του lead.</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2" aria-label="Προβολή χρεώσεων">
        <Button className="h-11" variant={view === "invoices" ? "default" : "outline"} aria-pressed={view === "invoices"} onClick={() => setView("invoices")}>Τιμολόγηση & λογιστήριο</Button>
        <Button className="h-11" variant={view === "overview" ? "default" : "outline"} aria-pressed={view === "overview"} onClick={() => setView("overview")}>Γενική εικόνα</Button>
      </div>

      {view === "invoices" && (period !== "custom" || Boolean(customFrom && customTo && customFrom <= customTo)) ? <BillingWorkspace input={queryInput} /> : view === "invoices" ? <p className="text-sm text-amber-800">Επιλέξτε έγκυρες ημερομηνίες.</p> : billingQuery.isLoading ? (
        <>
          <AdminMetricSkeletons count={4} />
          <Skeleton className="h-96 rounded-2xl" />
        </>
      ) : billingQuery.isError ? (
        <AdminErrorState message={billingQuery.error instanceof Error ? billingQuery.error.message : "Δεν φορτώθηκαν τα στοιχεία χρέωσης."} onRetry={() => void billingQuery.refetch()} />
      ) : data ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard title="Χρεώσιμα leads" value={formatNumber(data.summary.billable_leads)} description={`${formatNumber(data.summary.total_leads)} συνολικά στην περίοδο`} icon={<Users />} variant="info" size="compact" />
            <StatCard title="Συνολικό ποσό" value={formatCurrency(data.summary.total_amount)} description={`${formatCurrency(data.lead_price)} × ${formatNumber(data.summary.billable_leads)} leads`} icon={<Euro />} variant="success" size="compact" />
            <StatCard title="Συνεργάτες με χρέωση" value={formatNumber(data.summary.partners_with_leads)} description={`Μ.Ο. ${formatCurrency(data.summary.average_amount)} ανά συνεργάτη`} icon={<Users />} variant="default" size="compact" />
            <StatCard title="Χωρίς συνεργάτη" value={formatNumber(data.summary.unassigned_leads)} description="Δεν περιλαμβάνονται στη χρέωση" icon={<AlertTriangle />} variant={data.summary.unassigned_leads > 0 ? "warning" : "success"} size="compact" />
          </div>

          {data.summary.inactive_billable_leads > 0 && (
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-950">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
              <div>
                <p className="font-semibold">Ιστορικές χρεώσεις ανενεργών συνεργατών συμπεριλαμβάνονται κανονικά.</p>
                <p className="mt-1 leading-6 text-amber-800">{formatNumber(data.summary.inactive_billable_leads)} leads σε {formatNumber(data.summary.inactive_partners_with_leads)} ανενεργούς συνεργάτες. Η σημερινή κατάστασή τους δεν διαγράφει την ιστορική ανάθεση.</p>
              </div>
            </div>
          )}

          <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {data.rows.length === 0 ? (
                <AdminEmptyState title="Δεν υπάρχουν χρεώσεις" description="Δεν βρέθηκαν συνεργάτες για την επιλεγμένη περίοδο και τα φίλτρα." illustration="lead-insights" />
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="px-4 py-3 font-semibold">Συνεργάτης</th>
                          <th className="px-4 py-3 font-semibold">Κατάσταση</th>
                          <th className="px-4 py-3 text-right font-semibold">Leads</th>
                          <th className="px-4 py-3 text-right font-semibold">Ποσό</th>
                          <th className="px-4 py-3 text-right font-semibold">Τελευταίο lead</th>
                          <th className="w-14 px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows.map((row) => (
                          <tr key={row.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50/80">
                            <td className="px-4 py-3.5">
                              <p className="font-semibold text-slate-950">{row.name || "Χωρίς επωνυμία"}</p>
                              <p className="mt-0.5 max-w-md truncate text-xs text-slate-500">{row.location || "Δεν έχει δηλωθεί περιοχή"}</p>
                            </td>
                            <td className="px-4 py-3.5"><Badge variant={row.active ? "secondary" : "outline"} className={row.active ? "bg-emerald-50 text-emerald-700" : "text-slate-500"}>{row.active ? "Ενεργός" : "Ανενεργός"}</Badge></td>
                            <td className="px-4 py-3.5 text-right font-semibold text-slate-900">{formatNumber(row.lead_count)}</td>
                            <td className="px-4 py-3.5 text-right font-bold text-emerald-700">{formatCurrency(row.amount)}</td>
                            <td className="px-4 py-3.5 text-right text-slate-500">{displayDate(row.last_lead_at)}</td>
                            <td className="px-4 py-3.5 text-right"><Button asChild variant="ghost" size="icon" className="h-11 w-11"><Link to={`/admin/partners/${row.id}/dashboard`} aria-label={`Άνοιγμα ${row.name}`}><ArrowRight className="h-4 w-4" /></Link></Button></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold text-slate-900">
                          <td colSpan={2} className="px-4 py-4">Σύνολο εφαρμοσμένων φίλτρων</td>
                          <td className="px-4 py-4 text-right">{formatNumber(data.filtered_summary.billable_leads)}</td>
                          <td className="px-4 py-4 text-right text-emerald-700">{formatCurrency(data.filtered_summary.total_amount)}</td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-slate-500">{formatNumber(data.pagination.total)} συνεργάτες στα εφαρμοσμένα φίλτρα · Σελίδα {data.pagination.page} από {data.pagination.pages}</p>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" className="h-11 flex-1 gap-2 sm:flex-none" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1}><ChevronLeft className="h-4 w-4" /> Προηγούμενη</Button>
                      <Button type="button" variant="outline" className="h-11 flex-1 gap-2 sm:flex-none" onClick={() => setPage((value) => Math.min(data.pagination.pages, value + 1))} disabled={page >= data.pagination.pages}>Επόμενη <ChevronRight className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default BillingPage;
